import { registerSW } from 'virtual:pwa-register'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useI18n } from '../../i18n'
import { useOnlineStatus } from '../../lib/connectivity/useOnlineStatus'
import { hasUnsavedEdits } from '../../lib/session/unsavedGuard'
import { requestPersistentStorageIfSupported } from '../../lib/storage/persistentStorage'
import styles from './PwaShell.module.css'

const PERSIST_ACK_KEY = 'pwa-persist-denied-acked'

type Props = {
  children: ReactNode
}

function hasPersistAck(): boolean {
  try {
    return localStorage.getItem(PERSIST_ACK_KEY) === '1'
  } catch {
    return false
  }
}

export function PwaShell({ children }: Props) {
  const { t } = useI18n()
  const online = useOnlineStatus()
  const [updateReady, setUpdateReady] = useState(false)
  const [offlineReady, setOfflineReady] = useState(false)
  const [showPersistNotice, setShowPersistNotice] = useState(false)
  const updateSwRef = useRef<((reloadPage?: boolean) => Promise<void>) | undefined>(undefined)

  useEffect(() => {
    if (hasPersistAck()) return
    void requestPersistentStorageIfSupported().then((result) => {
      if (result === 'denied' && !hasPersistAck()) {
        setShowPersistNotice(true)
      }
    })
  }, [])

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return

    updateSwRef.current = registerSW({
      immediate: true,
      onNeedRefresh() {
        setUpdateReady(true)
      },
      onOfflineReady() {
        if (sessionStorage.getItem('pwa-offline-notified') !== '1') {
          setOfflineReady(true)
        }
      },
    })
  }, [])

  const ackPersistNotice = () => {
    try {
      localStorage.setItem(PERSIST_ACK_KEY, '1')
    } catch {
      /* private mode — still hide for this session */
    }
    setShowPersistNotice(false)
  }

  const applyUpdate = () => {
    if (hasUnsavedEdits()) {
      window.alert(t('pwa.updateBlocked'))
      return
    }
    void updateSwRef.current?.(true)
  }

  return (
    <>
      <a href="#main-content" className={styles.skipLink}>
        {t('pwa.skipLink')}
      </a>

      {!online && (
        <div className={`${styles.banner} ${styles.offline}`} role="status" aria-live="polite">
          {t('pwa.offline')}
        </div>
      )}

      {offlineReady && online && (
        <div className={`${styles.banner} ${styles.info}`} role="status" aria-live="polite">
          <span>{t('pwa.offlineReady')}</span>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              sessionStorage.setItem('pwa-offline-notified', '1')
              setOfflineReady(false)
            }}
          >
            {t('common.dismiss')}
          </button>
        </div>
      )}

      {updateReady && (
        <div
          className={`${styles.banner} ${styles.update}`}
          role="region"
          aria-label={t('pwa.updateRegion')}
        >
          <span>{t('pwa.updateReady')}</span>
          <button type="button" className="btn btn-primary" onClick={applyUpdate}>
            {t('pwa.reload')}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setUpdateReady(false)}
          >
            {t('pwa.notNow')}
          </button>
        </div>
      )}

      <main id="main-content" tabIndex={-1}>
        {children}
      </main>

      {showPersistNotice && (
        <div className={styles.persistDock} role="region" aria-label={t('pwa.persistDenied')}>
          <div className={styles.persistPanel}>
            <p className={styles.persistText}>{t('pwa.persistDenied')}</p>
            <div className={styles.persistActions}>
              <Link to="/data" className="btn btn-secondary" onClick={ackPersistNotice}>
                {t('data.title')}
              </Link>
              <button type="button" className="btn btn-primary" onClick={ackPersistNotice}>
                {t('common.ok')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
